import React from 'react';

/**
 * Queue Cost Analysis Component
 * Displays cost tracking and optimization recommendations
 */
const QueueCostAnalysis = ({ data, theme, timeRange }) => {
  if (!data) {
    return (
      <div className={`${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      } rounded-lg shadow border p-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          Cost Analysis
        </h3>
        <div className="flex items-center justify-center h-32">
          <p className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            No cost data available
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getSavingsColor = (savings) => {
    if (savings > 0) return 'text-green-500';
    if (savings < 0) return 'text-red-500';
    return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  };

  return (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          Cost Analysis & Optimization
        </h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          theme === 'dark' 
            ? 'bg-gray-700 text-gray-300' 
            : 'bg-gray-100 text-gray-700'
        }`}>
          {timeRange}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Current Costs */}
        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className={`text-sm font-medium mb-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Current Costs
          </h4>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                CPU Time
              </span>
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {formatCurrency(data.currentCosts?.cpuTime || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Database
              </span>
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {formatCurrency(data.currentCosts?.database || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                API Calls
              </span>
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {formatCurrency(data.currentCosts?.apiCalls || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Queue Operations
              </span>
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {formatCurrency(data.currentCosts?.queueOperations || 0)}
              </span>
            </div>
            
            <hr className={`${
              theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
            }`} />
            
            <div className="flex justify-between items-center">
              <span className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Total
              </span>
              <span className={`text-lg font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {formatCurrency(data.currentCosts?.total || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Projected Savings */}
        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className={`text-sm font-medium mb-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Queue Optimization
          </h4>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                CPU Reduction
              </span>
              <span className={`text-sm font-medium ${getSavingsColor(data.savings?.cpuReduction || 0)}`}>
                {formatCurrency(Math.abs(data.savings?.cpuReduction || 0))}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                DB Optimization
              </span>
              <span className={`text-sm font-medium ${getSavingsColor(data.savings?.databaseOptimization || 0)}`}>
                {formatCurrency(Math.abs(data.savings?.databaseOptimization || 0))}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Batch Processing
              </span>
              <span className={`text-sm font-medium ${getSavingsColor(data.savings?.batchProcessing || 0)}`}>
                {formatCurrency(Math.abs(data.savings?.batchProcessing || 0))}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Queue Overhead
              </span>
              <span className={`text-sm font-medium text-red-500`}>
                -{formatCurrency(data.projectedCosts?.queueOverhead || 0)}
              </span>
            </div>
            
            <hr className={`${
              theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
            }`} />
            
            <div className="flex justify-between items-center">
              <span className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Net Savings
              </span>
              <span className={`text-lg font-bold ${getSavingsColor(data.savings?.total || 0)}`}>
                {data.savings?.total > 0 ? '+' : ''}{formatCurrency(data.savings?.total || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Performance Impact */}
        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h4 className={`text-sm font-medium mb-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Performance Impact
          </h4>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Response Time
              </span>
              <span className={`text-sm font-medium ${
                (data.performanceImpact?.responseTimeImprovement || 0) > 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {formatPercentage(data.performanceImpact?.responseTimeImprovement || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Throughput
              </span>
              <span className={`text-sm font-medium ${
                (data.performanceImpact?.throughputImprovement || 0) > 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {formatPercentage(data.performanceImpact?.throughputImprovement || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Error Rate
              </span>
              <span className={`text-sm font-medium ${
                (data.performanceImpact?.errorRateReduction || 0) > 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {formatPercentage(-(data.performanceImpact?.errorRateReduction || 0))}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Resource Usage
              </span>
              <span className={`text-sm font-medium ${
                (data.performanceImpact?.resourceOptimization || 0) > 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {formatPercentage(-(data.performanceImpact?.resourceOptimization || 0))}
              </span>
            </div>
            
            <hr className={`${
              theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
            }`} />
            
            <div className="flex justify-between items-center">
              <span className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Overall Score
              </span>
              <span className={`text-lg font-bold ${
                (data.performanceImpact?.overallScore || 0) > 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {(data.performanceImpact?.overallScore || 0).toFixed(1)}/10
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="mt-6">
          <h4 className={`text-sm font-medium mb-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Optimization Recommendations
          </h4>
          
          <div className="space-y-2">
            {data.recommendations.map((recommendation, index) => (
              <div
                key={index}
                className={`flex items-start p-3 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'
                }`}
              >
                <div className="flex-shrink-0 mr-3">
                  <span className="text-blue-500">💡</span>
                </div>
                <div className="flex-1">
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {recommendation.message}
                  </p>
                  {recommendation.impact && (
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Potential impact: {recommendation.impact}
                    </p>
                  )}
                </div>
                {recommendation.priority && (
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
                    recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {recommendation.priority}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueCostAnalysis;
